<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert, useTrack } from 'dashboard/composables';
import { CAMPAIGN_TYPES } from 'shared/constants/campaign.js';
import { CAMPAIGNS_EVENTS } from 'dashboard/helper/AnalyticsHelper/events.js';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import CampaignWizard from './CampaignWizard.vue';

const store = useStore();
const { t } = useI18n();

const dialogRef = ref(null);
const discardDialogRef = ref(null);
const wizardRef = ref(null);
const isWizardDirty = ref(false);
const uiFlags = useMapGetter('campaigns/getUIFlags');
const isSubmitting = computed(() => uiFlags.value.isCreating);

const closeAndReset = () => {
  wizardRef.value?.reset();
  isWizardDirty.value = false;
  dialogRef.value?.close();
};

const buildPayload = wizardData => {
  const payload = {
    title: wizardData.title,
    message: wizardData.renderedTemplate || '',
    inbox_id: wizardData.inboxId,
    scheduled_at: wizardData.scheduledAt
      ? new Date(wizardData.scheduledAt).toISOString()
      : new Date().toISOString(),
    audience_type: wizardData.audienceType,
    conversation_label_id: wizardData.conversationLabelId || null,
    template_params: {
      ...wizardData.templateParams,
      processed_params: wizardData.processedParams || {},
      components: wizardData.selectedTemplate?.components || [],
    },
    throttle_config: wizardData.throttleConfig || { profile: 'moderate' },
  };

  if (wizardData.excludedLabels?.length > 0) {
    payload.excluded_labels = wizardData.excludedLabels;
  }

  if (wizardData.audienceType === 'label') {
    payload.audience = wizardData.selectedAudience.map(id => ({
      id,
      type: 'Label',
    }));
  } else if (wizardData.audienceType === 'csv_import') {
    payload.import_id = wizardData.importId;
    payload.column_mapping = {
      ...wizardData.columnMapping,
      ...(wizardData.autoLabel ? { auto_label: wizardData.autoLabel } : {}),
    };
  }

  return payload;
};

const handleSubmit = async wizardData => {
  try {
    const campaignPayload = buildPayload(wizardData);
    await store.dispatch('campaigns/create', campaignPayload);

    useTrack(CAMPAIGNS_EVENTS.CREATE_CAMPAIGN, {
      type: CAMPAIGN_TYPES.ONE_OFF,
    });

    useAlert(t('CAMPAIGN.WHATSAPP.CREATE.FORM.API.SUCCESS_MESSAGE'));
    closeAndReset();
  } catch (error) {
    const errorMessage =
      error?.response?.message ||
      t('CAMPAIGN.WHATSAPP.CREATE.FORM.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};

// Backdrop, Esc and the wizard's cancel button all route here. Confirm before
// discarding when there is unsaved progress; otherwise close right away.
const handleCloseRequest = () => {
  if (isWizardDirty.value) {
    discardDialogRef.value?.open();
  } else {
    closeAndReset();
  }
};

const confirmDiscard = () => {
  discardDialogRef.value?.close();
  closeAndReset();
};

defineExpose({ dialogRef });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('CAMPAIGN.WHATSAPP.CREATE.TITLE')"
    width="5xl"
    overflow-y-auto
    :confirm-before-close="isWizardDirty"
    :show-confirm-button="false"
    :show-cancel-button="false"
    @close-requested="handleCloseRequest"
  >
    <CampaignWizard
      ref="wizardRef"
      :is-submitting="isSubmitting"
      @submit="handleSubmit"
      @cancel="handleCloseRequest"
      @dirty-change="isWizardDirty = $event"
    />
  </Dialog>
  <Dialog
    ref="discardDialogRef"
    type="alert"
    :title="t('CAMPAIGN.WHATSAPP.CREATE.DISCARD.TITLE')"
    :description="t('CAMPAIGN.WHATSAPP.CREATE.DISCARD.DESCRIPTION')"
    :confirm-button-label="t('CAMPAIGN.WHATSAPP.CREATE.DISCARD.CONFIRM')"
    :cancel-button-label="t('CAMPAIGN.WHATSAPP.CREATE.DISCARD.CANCEL')"
    @confirm="confirmDiscard"
  />
</template>
